import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take, catchError } from 'rxjs/operators';
import { ReportBuilderService } from '../../services/report-builder.service';
import { SavedReportService } from '../../services/saved-report.service';
import { ReportEntitySchema } from '../../models/report-builder-schema.model';
import { SavedReport } from '../../models/saved-report.model';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridSearchFilter } from 'src/app/core/models/grid-search-filter.model';

/** Everything needed to rebuild this page's picker state on reload. */
interface BasicBuilderState {
  columns: string[];
  relationships: string[];
}

/**
 * "Basic" mode: no aggregation, just field/filter/join picking against one entity's own CRUD
 * endpoint. Almost everything here already exists in `<app-grid>` (filtering, joins, sorting,
 * pagination, CSV export) - this page's only job is turning the whitelist schema plus the user's
 * picks into that component's inputs.
 */
@Component({
  selector: 'app-report-builder-basic',
  templateUrl: './report-builder-basic.component.html',
  styleUrls: ['./report-builder-basic.component.scss'],
  standalone: false
})
export class ReportBuilderBasicComponent implements OnInit {

  entityKey: string = '';
  schema: ReportEntitySchema = null;
  loadFailed: boolean = false;

  activeColumnKeys: Record<string, boolean> = {};
  activeRelationships: Record<string, boolean> = {};

  columns: GridColumn[] = [];
  relationships: string[] = [];
  searchFields: string[] = [];
  searchFilters: GridSearchFilter[] = [];

  built: boolean = false;

  savedReports: SavedReport[] = [];
  selectedSavedReportId: number = -1;
  currentSavedReport: SavedReport = null;
  reportName: string = '';
  saving: boolean = false;
  saveFailed: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private reportBuilderService: ReportBuilderService,
    private savedReportService: SavedReportService,
  ) { }

  ngOnInit(): void {
    this.entityKey = this.route.snapshot.paramMap.get('entity');

    this.reportBuilderService.getSchema()
      .pipe(take(1))
      .pipe(catchError((e) => { this.loadFailed = true; return e; }))
      .subscribe((schemas: ReportEntitySchema[]) => {
        this.schema = (schemas || []).find((s) => s.key === this.entityKey) || null;

        if (this.schema === null) {
          this.loadFailed = true;
          return;
        }

        this.schema.columns.forEach((c) => { this.activeColumnKeys[c.key] = true; });
        this.schema.basicRelationships.forEach((r) => { this.activeRelationships[r] = false; });

        this.savedReportService.getAllByEntity(this.schema.key)
          .pipe(take(1))
          .pipe(catchError((e) => e))
          .subscribe((reports: SavedReport[]) => {
            this.savedReports = (reports || []).filter((r) => r.mode === 'basic');
          });
      });
  }

  toggleColumn(key: string): void {
    this.activeColumnKeys[key] = !this.activeColumnKeys[key];
  }

  toggleRelationship(key: string): void {
    this.activeRelationships[key] = !this.activeRelationships[key];
  }

  runReport(): void {
    this.columns = this.schema.columns
      .filter((c) => this.activeColumnKeys[c.key])
      .map((c) => ({ active: true, label: c.label, field: c.key, value: null } as GridColumn));

    this.relationships = this.schema.basicRelationships.filter((r) => this.activeRelationships[r]);

    this.searchFields = this.schema.columns
      .filter((c) => this.activeColumnKeys[c.key] && (c.type === 'string' || c.type === 'enum'))
      .map((c) => c.key);

    this.searchFilters = this.schema.columns.map((c) => {
      const filter = new GridSearchFilter();
      filter.field = c.key;
      filter.label = c.label;
      filter.type = c.type === 'date' ? 'date' : c.type === 'number' ? 'number' : 'text';
      return filter;
    });

    this.built = true;
  }

  saveReport(): void {
    if (!this.schema || !this.reportName || this.reportName.trim() === '') {
      return;
    }

    const state: BasicBuilderState = {
      columns: Object.keys(this.activeColumnKeys).filter((k) => this.activeColumnKeys[k]),
      relationships: Object.keys(this.activeRelationships).filter((k) => this.activeRelationships[k]),
    };

    const report = this.currentSavedReport ? { ...this.currentSavedReport } as SavedReport : new SavedReport();
    report.name = this.reportName;
    report.mode = 'basic';
    report.entity = this.schema.key;
    report.spec = JSON.stringify(state);

    this.saving = true;
    this.saveFailed = false;

    const save$ = report.id > 0 ? this.savedReportService.update(report) : this.savedReportService.create(report);

    save$
      .pipe(take(1))
      .pipe(catchError((e) => { this.saving = false; this.saveFailed = true; return e; }))
      .subscribe((saved: SavedReport) => {
        this.saving = false;
        this.currentSavedReport = saved;
        this.selectedSavedReportId = saved.id;

        const existingIndex = this.savedReports.findIndex((r) => r.id === saved.id);
        if (existingIndex === -1) {
          this.savedReports.push(saved);
        } else {
          this.savedReports[existingIndex] = saved;
        }
      });
  }

  newReport(): void {
    this.currentSavedReport = null;
    this.selectedSavedReportId = -1;
    this.reportName = '';
  }

  loadSavedReport(id: number): void {
    const report = this.savedReports.find((r) => r.id === Number(id));

    if (!report) {
      return;
    }

    const state: BasicBuilderState = JSON.parse(report.spec);

    this.currentSavedReport = report;
    this.selectedSavedReportId = report.id;
    this.reportName = report.name;

    this.activeColumnKeys = {};
    this.schema.columns.forEach((c) => { this.activeColumnKeys[c.key] = state.columns.indexOf(c.key) !== -1; });

    this.activeRelationships = {};
    this.schema.basicRelationships.forEach((r) => { this.activeRelationships[r] = state.relationships.indexOf(r) !== -1; });

    this.built = false;
  }

  deleteSavedReport(): void {
    if (!this.currentSavedReport) {
      return;
    }

    this.savedReportService.delete(this.currentSavedReport.id)
      .pipe(take(1))
      .pipe(catchError((e) => e))
      .subscribe(() => {
        this.savedReports = this.savedReports.filter((r) => r.id !== this.currentSavedReport.id);
        this.newReport();
      });
  }
}
